import React, { useState, useRef, useEffect } from "react";

interface PinSwitchProps {
  currentUserName: string;
  onPinSubmit: (pin: string) => Promise<void>;
  onCancel: () => void;
}

export default function PinSwitch({ currentUserName, onPinSubmit, onCancel }: PinSwitchProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleDigit = (digit: string) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);
    setError("");

    // Auto-submit when 4 digits entered
    if (newPin.length === 4) {
      submitPin(newPin);
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
    setError("");
  };

  const submitPin = async (pinValue: string) => {
    setLoading(true);
    try {
      await onPinSubmit(pinValue);
    } catch {
      setError("Invalid PIN");
      setPin("");
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
      return;
    }
    if (e.key === "Backspace") {
      handleBackspace();
      return;
    }
    if (/^\d$/.test(e.key)) {
      handleDigit(e.key);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(10, 25, 47, 0.95)" }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      ref={(el) => el?.focus()}
    >
      <div
        className="rounded-2xl shadow-2xl w-full max-w-xs p-6 text-center"
        style={{ background: "var(--rbw-bg-card)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
          Switch Cashier
        </div>
        <div className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
          Currently: <span className="font-bold" style={{ color: "var(--rbw-navy)" }}>{currentUserName}</span>
        </div>

        <div className="text-sm font-semibold mb-3" style={{ color: "var(--rbw-navy)" }}>
          Enter your 4-digit PIN
        </div>

        {/* PIN dots display */}
        <div className="flex justify-center gap-3 mb-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-4 h-4 rounded-full transition-all"
              style={{
                background: i < pin.length ? "var(--rbw-navy)" : "var(--rbw-border)",
                transform: i < pin.length ? "scale(1.2)" : "scale(1)",
              }}
            />
          ))}
        </div>

        {/* Hidden input for keyboard capture; its keys bubble up to the
            overlay's handler, so it must not handle them itself too */}
        <input
          ref={inputRef}
          type="text"
          className="opacity-0 absolute"
          readOnly
        />

        {error && (
          <p className="text-xs font-bold mb-3" style={{ color: "var(--rbw-danger)" }}>
            {error}
          </p>
        )}

        {loading && (
          <p className="text-xs font-medium mb-3" style={{ color: "var(--rbw-blue)" }}>
            Switching...
          </p>
        )}

        {/* Number pad */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", ""].map((digit, i) => {
            if (digit === "" && i === 9) {
              return <div key={i} />;
            }
            if (digit === "" && i === 11) {
              return (
                <button
                  key="backspace"
                  onClick={handleBackspace}
                  className="py-3 rounded-xl font-bold text-lg transition-all active:scale-95"
                  style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
                >
                  &larr;
                </button>
              );
            }
            return (
              <button
                key={digit}
                onClick={() => handleDigit(digit)}
                disabled={loading}
                className="py-3 rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-50"
                style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)", border: "2px solid var(--rbw-border)" }}
              >
                {digit}
              </button>
            );
          })}
        </div>

        <button
          onClick={onCancel}
          className="w-full py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95"
          style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)" }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
